import { emitSuccessToast, emitWarningToast } from '@seeneco-ui/core';

import { LeadCheckCode } from '@api/leads/model';
import { SendEmailNotificationOptions } from '@api/security';
import { SendSmsOptions } from '@core/api/atr-framework/model';
import { CompanyCard } from '@core/api/business-hub/model';
import { createAsyncAction, createStaticAction, createTypes } from '@flux';
import { mainLeadsSelectorsPack } from '@leads/selectors';
import { mainPlatformSelectorsPack } from '@platform/selectors';

type ActionTypes = {
	ADD_LEAD: string;
	FETCH_COMPANY_CARD_BY_TAXCODE: string;
	FETCH_LEADS: string;
	FETCH_NEW_LEAD_SMS_TEXT: string;
	INVALIDATE_LEADS: string;
	SEND_EMAIL_NOTIFICATION: string;
	SEND_SMS_NOTIFICATION: string;
	SET_DATE_RANGE: string;
	UPDATE_LEAD: string;
};

const types = createTypes<ActionTypes>(
	[
		'ADD_LEAD',
		'FETCH_COMPANY_CARD_BY_TAXCODE',
		'FETCH_LEADS',
		'FETCH_NEW_LEAD_SMS_TEXT',
		'INVALIDATE_LEADS',
		'SEND_EMAIL_NOTIFICATION',
		'SEND_SMS_NOTIFICATION',
		'SET_DATE_RANGE',
		'UPDATE_LEAD',
	],
	'LEADS',
);

const actions = {
	addLead: createAsyncAction(types.ADD_LEAD)((box, lead: Lead, onComplete: (code: LeadCheckCode) => void) =>
		box.api.leadsPack.addLead(lead, onComplete),
	) as (lead: Lead, onComplete: (code: LeadCheckCode) => void) => void,
	fetchCompanyCardByTaxCode: createAsyncAction(types.FETCH_COMPANY_CARD_BY_TAXCODE)(
		(box, taxCode: string, onComplete: (companyCard: CompanyCard) => void) =>
			box.api.businessHub.fetchCompanyCardByTaxCode(taxCode, onComplete),
	) as (taxCode: string, onComplete: (companyCard: CompanyCard) => void) => void,
	fetchLeads: createAsyncAction(types.FETCH_LEADS)(
		(box, dateRange: DateRange) => box.api.leadsPack.fetchLeads(dateRange),
		{
			cache: {
				asyncSelector: mainLeadsSelectorsPack.selectAsyncLeads,
			},
		},
	) as (dateRange: DateRange) => void,
	fetchNewLeadSmsText: createAsyncAction(types.FETCH_NEW_LEAD_SMS_TEXT)(
		box => box.api.preferencesApi.fetchNewLeadSmsText(mainPlatformSelectorsPack.selectTenantID(box.getState())),
		{
			cache: {
				asyncSelector: mainLeadsSelectorsPack.selectAsyncNewLeadSmsText,
			},
		},
	) as () => void,
	invalidateLeads: () => createStaticAction(types.INVALIDATE_LEADS)(),
	sendEmailNotification: createAsyncAction(types.SEND_EMAIL_NOTIFICATION)(
		(box, options: SendEmailNotificationOptions) => {
			return new Promise(resolve => {
				box.api.securityPack.directory.sendEmailNotification(options).then(result => {
					if (result) {
						emitSuccessToast({
							content: 'E-mail успешно отправлен!',
						});
					} else {
						emitWarningToast({
							content: 'Во время отправки e-mail что-то пошло не так...',
						});
					}

					resolve(true);
				});
			});
		},
	) as (options: SendEmailNotificationOptions) => void,
	sendSmsNotification: createAsyncAction(types.SEND_SMS_NOTIFICATION)((box, options: SendSmsOptions) => {
		return new Promise<void>(resolve => {
			box.api.atrFramework
				.sendSms(options)
				.then(result => {
					if (result?.Sent) {
						emitSuccessToast({
							content: 'СМС успешно отправлено!',
						});
					} else {
						emitWarningToast({
							content: 'Во время отправки СМС что-то пошло не так...',
						});
					}

					resolve();
				})
				.catch(() =>
					emitWarningToast({
						content: 'Во время отправки СМС что-то пошло не так...',
					}),
				);
		});
	}) as (options: SendSmsOptions) => void,
	setDateRange: (dateRange: DateRange) => createStaticAction(types.SET_DATE_RANGE)(dateRange),
	updateLead: createAsyncAction(types.UPDATE_LEAD)((box, lead: Lead) => box.api.leadsPack.updateLead(lead)) as (
		lead: Lead,
	) => void,
};

export const mainLeadsActionsPack = {
	actions,
	types,
};
